import type { SQLiteDatabase } from 'expo-sqlite';

import { newId, now } from '@/db/ids';
import type {
  Basement,
  ClimateZone,
  ExteriorMaterial,
  Foundation,
  Garage,
  HomeType,
  HvacType,
  LotBand,
  RegionHazard,
  RoofMaterial,
  Sewer,
  SetupMode,
  WaterSource,
  YearBand,
} from '@/db/types';
import {
  defaultSeasonalRange,
  type SeasonalRange,
} from '@/lib/seasonal';
import { expandOnboardingKey, type PlannedTask } from '@/lib/plan';
import type { HeatingFuel, QtyMap } from '@/store/onboarding';

export interface CommitInput {
  mode: SetupMode;
  ownerFirstName?: string | null;
  ownerLastName?: string | null;
  /** Optional home name (nickname). Captured on the home-type screen. */
  homeName?: string | null;
  zip: string;
  address?: string | null;
  lat?: number | null;
  lon?: number | null;
  hasPool?: boolean;
  hasSolar?: boolean;
  hasPets?: boolean;
  hasKids?: boolean;
  hasHoa?: boolean;
  hasWoodfire?: boolean;
  hasSmartHome?: boolean;
  hasStrHost?: boolean;
  hasHeavySnow?: boolean;
  hasGarden?: boolean;
  hasAccessibility?: boolean;
  isFirstTime?: boolean;
  // Schema v35 lifestyle additions.
  travelsOften?: boolean;
  worksFromHome?: boolean;
  hasAllergies?: boolean;
  hasNewborn?: boolean;
  multiGenHousehold?: boolean;
  over60Owner?: boolean;
  hasLivestock?: boolean;
  hasShortTermContractors?: boolean;
  climateZone: ClimateZone | null;
  homeType: HomeType | null;
  yearBand: YearBand | null;
  /** Specific build year captured on step 2 of onboarding. When set,
   *  it's the authoritative year; yearBand is derived from it. Null
   *  means the user skipped (or this came from an older flow that only
   *  collected the band). */
  yearBuilt?: number | null;
  sqft: number;
  hvacType: HvacType | null;
  /** Primary heating fuel — paired with HVAC type on the same onboarding
   *  step. Disambiguates oil-tank / propane / gas-line task firing. */
  heatingFuel?: HeatingFuel | null;
  /** Year the user took ownership of the home (schema v35). Drives
   *  onboarding tone — fresh-owner discovery vs long-term tracker. */
  ownedSince?: number | null;
  lotBand: LotBand | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  stories?: number | null;
  basement?: Basement | null;
  foundation?: Foundation | null;
  roofMaterial?: RoofMaterial | null;
  exteriorMaterial?: ExteriorMaterial | null;
  /** Multi-value arrays (schema v32). Mixed homes use these; the
   *  single-value fields above are kept in sync with the first
   *  element for back-compat. */
  roofMaterials?: RoofMaterial[];
  exteriorMaterials?: ExteriorMaterial[];
  foundations?: Foundation[];
  garage?: Garage | null;
  waterSource?: WaterSource | null;
  sewer?: Sewer | null;
  /** Regional natural-hazard tags (schema v35) → property.region_hazards_json. */
  regionHazards?: RegionHazard[];
  inside: QtyMap;
  outside: QtyMap;
  tasks: PlannedTask[];
}

/**
 * Writes the result of an onboarding session — property + selected systems
 * + initial scheduled tasks — in a single transaction.
 *
 * The `inside` / `outside` qty maps support multi-instance: a count of 3 on
 * `fans` creates 3 system rows, each addressable independently. Aggregate
 * keys like `laundry` are expanded into their canonical kinds (`washer` +
 * `dryer`) before counts are applied.
 */
export async function commitOnboarding(
  db: SQLiteDatabase,
  input: CommitInput
): Promise<string> {
  const propertyId = newId();
  const t = now();
  // Track one representative system id per kind for task linking. With
  // multi-instance we link the task to the *first* created row; future
  // refinement can spread tasks across instances explicitly.
  const firstSystemIdByKind = new Map<string, string>();

  // Diagnostic: pre-flight check that the property table has every column
  // the INSERT references. Catches the case where a schema migration didn't
  // run (or partially ran), surfacing a precise "missing column X" error
  // instead of SQLite's generic "Error finalizing statement."
  const requiredCols = [
    'id', 'zip', 'address', 'lat', 'lon', 'climate_zone', 'home_type',
    'year_band', 'year_built', 'sqft', 'hvac_type', 'heating_fuel',
    'owned_since', 'region_hazards_json', 'lot_band', 'has_pool', 'has_solar',
    'has_pets', 'has_kids', 'has_hoa', 'has_woodfire', 'has_smart_home',
    'has_str_host', 'has_heavy_snow', 'has_garden', 'has_accessibility',
    'is_first_time', 'travels_often', 'works_from_home', 'has_allergies',
    'has_newborn', 'multi_gen_household', 'over_60_owner', 'has_livestock',
    'has_short_term_contractors', 'bedrooms', 'bathrooms', 'stories',
    'basement', 'foundation', 'roof_material', 'exterior_material', 'garage',
    'water_source', 'sewer', 'roof_materials_json', 'exterior_materials_json',
    'foundations_json', 'owner_first_name', 'owner_last_name', 'name',
    'show_weather', 'notification_frequency', 'setup_mode', 'setup_complete',
    'created_at', 'updated_at',
  ];
  const cols = await db.getAllAsync<{ name: string }>(
    `PRAGMA table_info(property)`
  );
  const have = new Set(cols.map((c) => c.name));
  const missing = requiredCols.filter((c) => !have.has(c));
  if (missing.length > 0) {
    throw new Error(
      `Property table missing columns: ${missing.join(', ')}. ` +
      `DB has ${cols.length} columns; migration didn't add all of them.`
    );
  }

  // Diagnostic wrapper — labels each statement so the user sees
  // "Couldn't save: PROPERTY INSERT: <real error>" instead of the
  // anonymous "Error finalizing statement."
  const labelled = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      throw new Error(`${label}: ${msg}`);
    }
  };

  await db.withTransactionAsync(async () => {
    await labelled('PROPERTY INSERT', async () => db.runAsync(
      `INSERT INTO property (
        id, zip, address, lat, lon, climate_zone, home_type, year_band, year_built, sqft, hvac_type,
        heating_fuel, owned_since, region_hazards_json,
        lot_band, has_pool, has_solar, has_pets, has_kids,
        has_hoa, has_woodfire, has_smart_home, has_str_host,
        has_heavy_snow, has_garden, has_accessibility, is_first_time,
        travels_often, works_from_home, has_allergies, has_newborn,
        multi_gen_household, over_60_owner, has_livestock, has_short_term_contractors,
        bedrooms, bathrooms, stories, basement, foundation, roof_material,
        exterior_material, garage, water_source, sewer,
        roof_materials_json, exterior_materials_json, foundations_json,
        owner_first_name, owner_last_name, name,
        show_weather, notification_frequency,
        setup_mode, setup_complete, created_at, updated_at
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1, ?, ?)`,
      propertyId,
      input.zip || null,
      input.address ?? null,
      input.lat ?? null,
      input.lon ?? null,
      input.climateZone,
      input.homeType,
      // Prefer the band the user explicitly picked. Fall back to deriving
      // it from the precise year so older surfaces that read year_band
      // (rule engine etc.) still work when only year_built was captured.
      input.yearBand ?? yearBandFromYearBuilt(input.yearBuilt ?? null),
      input.yearBuilt ?? null,
      input.sqft,
      input.hvacType,
      input.heatingFuel ?? null,
      input.ownedSince ?? null,
      input.regionHazards && input.regionHazards.length > 0
        ? JSON.stringify(input.regionHazards)
        : null,
      input.lotBand,
      input.hasPool ? 1 : 0,
      input.hasSolar ? 1 : 0,
      input.hasPets ? 1 : 0,
      input.hasKids ? 1 : 0,
      input.hasHoa ? 1 : 0,
      input.hasWoodfire ? 1 : 0,
      input.hasSmartHome ? 1 : 0,
      input.hasStrHost ? 1 : 0,
      input.hasHeavySnow ? 1 : 0,
      input.hasGarden ? 1 : 0,
      input.hasAccessibility ? 1 : 0,
      input.isFirstTime ? 1 : 0,
      input.travelsOften ? 1 : 0,
      input.worksFromHome ? 1 : 0,
      input.hasAllergies ? 1 : 0,
      input.hasNewborn ? 1 : 0,
      input.multiGenHousehold ? 1 : 0,
      input.over60Owner ? 1 : 0,
      input.hasLivestock ? 1 : 0,
      input.hasShortTermContractors ? 1 : 0,
      input.bedrooms ?? null,
      input.bathrooms ?? null,
      input.stories ?? null,
      input.basement ?? null,
      input.foundation ?? null,
      input.roofMaterial ?? null,
      input.exteriorMaterial ?? null,
      input.garage ?? null,
      input.waterSource ?? null,
      input.sewer ?? null,
      // Multi-value v32 arrays — null when user picked nothing (or
      // skipped the page). Schema migration seeded these from the
      // legacy single-value columns for existing properties.
      input.roofMaterials && input.roofMaterials.length > 0
        ? JSON.stringify(input.roofMaterials)
        : null,
      input.exteriorMaterials && input.exteriorMaterials.length > 0
        ? JSON.stringify(input.exteriorMaterials)
        : null,
      input.foundations && input.foundations.length > 0
        ? JSON.stringify(input.foundations)
        : null,
      input.ownerFirstName?.trim() || null,
      input.ownerLastName?.trim() || null,
      // Wave 74 (user call): an unnamed home defaults to "{Last} Home"
      // — personal without forcing a decision. The placeholder
      // suggestions in onboarding stay soft.
      input.homeName?.trim() ||
        (input.ownerLastName?.trim() ? `${input.ownerLastName.trim()} Home` : null),
      // Defaults for new properties: weather panel OFF (user opts in via
      // Settings → Preferences); notifications default to daily frequency.
      0,
      'daily',
      input.mode,
      t,
      t
    ));

    // Hemisphere auto-detect from latitude. Lat < 0 = Southern hemisphere
    // → flip season anchors so a "spring" rule fires Sept-Nov instead of
    // Mar-May. Lat null or >= 0 leaves the schema default ('northern')
    // alone — matches the existing US-built catalog for the common case.
    if (typeof input.lat === 'number' && input.lat < 0) {
      await db.runAsync(
        `UPDATE property SET hemisphere = 'southern' WHERE id = ?`,
        propertyId
      );
    }

    const allPicks: { tileKey: string; count: number; loc: 'inside' | 'outside' }[] = [
      ...Object.entries(input.inside)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => ({ tileKey: k, count: n, loc: 'inside' as const })),
      ...Object.entries(input.outside)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => ({ tileKey: k, count: n, loc: 'outside' as const })),
    ];

    for (const pick of allPicks) {
      // Inherit install year from the home's build year by default. The
      // user said "filling in years on every system is annoying" — most
      // structural systems and original appliances WERE installed
      // when the home was built, so the home's exact year (when known)
      // or the band's midpoint gets us 95% right for zero effort. The
      // user can override per-system later via the walkthrough or
      // system-detail screen.
      const inheritedYear =
        input.yearBuilt ?? yearBandMidpointYear(input.yearBand);

      for (const kind of expandOnboardingKey(pick.tileKey)) {
        // Pass the climate zone so pool gets Jun–Sep in cold regions
        // (Memorial Day → Labor Day in the Midwest) and year-round in
        // hot regions, instead of the generic May–Sep default.
        const range: SeasonalRange = defaultSeasonalRange(
          kind,
          input.climateZone ?? null
        );
        for (let i = 0; i < pick.count; i++) {
          const id = newId();
          if (!firstSystemIdByKind.has(kind)) firstSystemIdByKind.set(kind, id);
          await labelled(`SYSTEM INSERT (${kind})`, async () => db.runAsync(
            `INSERT INTO system (
              id, property_id, kind, location, installed_year, is_active,
              active_start_month, active_end_month, created_at, updated_at
            ) VALUES (?, ?, ?, ?, ?, 1, ?, ?, ?, ?)`,
            id,
            propertyId,
            kind,
            pick.loc,
            inheritedYear,
            range.startMonth,
            range.endMonth,
            t,
            t
          ));
        }
      }
    }

    for (const task of input.tasks) {
      const id = newId();
      const systemId =
        task.system_kind && firstSystemIdByKind.get(task.system_kind)
          ? firstSystemIdByKind.get(task.system_kind)!
          : null;
      await labelled(`TASK INSERT (${task.rule_key})`, async () => db.runAsync(
        `INSERT INTO task (
          id, property_id, system_id, rule_key, title, why, time_minutes,
          difficulty, season, due_at, tier, icon, created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        id,
        propertyId,
        systemId,
        task.rule_key,
        task.title,
        task.why,
        task.time_minutes,
        task.difficulty,
        task.season,
        task.due_at,
        task.tier,
        task.icon,
        t,
        t
      ));
    }
  });

  return propertyId;
}

/**
 * Map a year-band onboarding answer to a representative integer year.
 * Used to pre-fill `installed_year` on systems created during
 * onboarding — most structural systems and original appliances were
 * installed when the home was built, so a midpoint year gets us close
 * for zero effort. The user can override per-system later.
 *
 * The returned year is the rough midpoint of each band:
 *   pre60   → 1955 (pre-1960 is a wide band; 1955 reads as "yes, old")
 *   60-80   → 1970
 *   90-00   → 1995 (note: skips the 1980s — that's how the band was named)
 *   2010+   → 2015 (modern enough that most systems are < 15 yr old)
 *   unsure  → null (don't fake what the user told us they don't know)
 *
 * Returning null when unsure means scheduling tier-based rules
 * (e.g. roof inspection at year 20) won't fire from a fabricated year.
 */
export function yearBandMidpointYear(band: YearBand | null): number | null {
  switch (band) {
    case 'pre60': return 1955;
    case '60-80': return 1970;
    case '90-00': return 1995;
    case '2010+': return 2015;
    default: return null;
  }
}

/**
 * Inverse of yearBandMidpointYear — collapse a specific year into the
 * coarse band the legacy rule engine still uses. Lets us collect the
 * precise year on the new home-type onboarding step and still feed
 * year_band-based rules without breaking them.
 */
export function yearBandFromYearBuilt(year: number | null): YearBand | null {
  if (year === null || !Number.isFinite(year)) return null;
  if (year < 1960) return 'pre60';
  if (year < 1990) return '60-80';
  if (year < 2010) return '90-00';
  return '2010+';
}
